import { ensureError, ensureHelp, ensureLabel, INPUT_BASE_CLASS, type FieldType } from './base.js';
import { validateRequired } from '../validation.js';
import { withCustomMessage } from './text.js';
import { loadChunk } from '../../core/lazy.js';
import { t } from '../../i18n/index.js';

/**
 * Контракт рантайма phone-чанка. Чанк сам выставляет
 * `window.__plpRuntime.phone`.
 */
export interface PhoneRuntime {
  /** Прикрепить маску, валидацию и флаги к input'у */
  attach(
    input: HTMLInputElement,
    options: { defaultCountry?: string }
  ): {
    /** Текущее E.164 значение или '' */
    getE164(): string;
    /** true если поле валидно ИЛИ пустое */
    isValid(): boolean;
    destroy(): void;
  };
}

declare global {
  interface Window {
    __plpRuntime?: { phone?: PhoneRuntime } & Record<string, unknown>;
  }
}

interface PhoneState {
  attached?: ReturnType<PhoneRuntime['attach']>;
  promise?: Promise<void>;
}
const STATE = new WeakMap<HTMLInputElement, PhoneState>();

export const phoneType: FieldType = {
  render(ctx) {
    ensureLabel(ctx.host, ctx.label, ctx.required);

    let input = ctx.host.querySelector<HTMLInputElement>('input[type="tel"]');
    if (!input) {
      input = document.createElement('input');
      input.className = INPUT_BASE_CLASS;
      ctx.host.insertBefore(input, ctx.host.querySelector(':scope > .plp-field-error'));
    }
    input.type = 'tel';
    input.inputMode = 'tel';
    input.autocomplete = 'tel';
    input.name = ctx.name;
    input.required = ctx.required;
    if (ctx.placeholder !== undefined) {
      input.placeholder = ctx.placeholder;
    } else if (!input.placeholder) {
      input.placeholder = '+7 999 123-45-67';
    }
    if (ctx.defaultValue && !input.value) input.value = ctx.defaultValue;

    ensureError(ctx.host);
    ensureHelp(ctx.host, ctx.help);

    // До загрузки чанка — нативный <input type="tel">. Маска и live-валидация
    // подключаются после загрузки phone-чанка (lazy).
    const state = STATE.get(input) ?? {};
    if (!state.attached && !state.promise) {
      state.promise = loadChunk('phone')
        .then(() => {
          const runtime = window.__plpRuntime?.phone;
          if (!runtime) return;
          state.attached = runtime.attach(input!, {
            defaultCountry: ctx.host.getAttribute('country') ?? undefined,
          });
        })
        .catch(() => {
          /* fallback: input работает без маски */
        });
      STATE.set(input, state);
    }
  },

  getValue(ctx) {
    const input = ctx.host.querySelector<HTMLInputElement>('input[type="tel"]');
    if (!input) return '';
    const state = STATE.get(input);
    if (state?.attached) {
      const e164 = state.attached.getE164();
      if (e164) return e164;
    }
    // Fallback (чанк не успел загрузиться): возвращаем сырое значение,
    // нормализуем явно.
    return normalizeRaw(input.value);
  },

  validate(ctx) {
    const input = ctx.host.querySelector<HTMLInputElement>('input[type="tel"]');
    const raw = input?.value.trim() ?? '';
    if (ctx.required) {
      const r = validateRequired(raw);
      if (!r.valid) return withCustomMessage(r, ctx);
    }
    if (!raw) return { valid: true };
    if (input) {
      const state = STATE.get(input);
      if (state?.attached && !state.attached.isValid()) {
        return withCustomMessage({ valid: false, error: t('form.validation.phone') }, ctx);
      }
    }
    // Без чанка — минимальная sanity-проверка длины.
    const digits = raw.replace(/\D/g, '');
    if (digits.length < 6) {
      return withCustomMessage({ valid: false, error: t('form.validation.phone') }, ctx);
    }
    return { valid: true };
  },

  shouldSubmit() {
    return true;
  },

  setDisabled(ctx, disabled) {
    const input = ctx.host.querySelector<HTMLInputElement>('input[type="tel"]');
    if (input) input.disabled = disabled;
  },
};

function normalizeRaw(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (!digits) return '';
  return value.startsWith('+') ? `+${digits}` : `+${digits}`;
}
